// 配信中セッション一覧(設計書 §3.5.5)
// 強制停止可能(理由必須)

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiClientError } from '../../api/client';
import {
  ADMIN_SESSION_STATE_LABELS,
  useActiveSessions,
  useForceStopSession,
  type ActiveSession,
} from '../../api/admin/sessions';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';

export function AdminActiveSessionsPage() {
  const sessionsQuery = useActiveSessions();
  const forceStop = useForceStopSession();
  const [forcing, setForcing] = useState<{ session: ActiveSession; reason: string } | null>(null);

  return (
    <AdminLayout
      title="配信中セッション"
      description="現在配信中のセッション一覧です。5 秒ごとに更新します。"
    >
      {sessionsQuery.isLoading && !sessionsQuery.data && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {sessionsQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {sessionsQuery.error instanceof ApiClientError
              ? sessionsQuery.error.message
              : '取得に失敗しました'}
          </p>
        </Card>
      )}

      {sessionsQuery.data && sessionsQuery.data.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            現在配信中のセッションはありません。
          </p>
        </Card>
      )}

      {sessionsQuery.data?.map((session) => (
        <Card key={session.ulid}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 16,
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>
                  <Link
                    to={`/admin/performers/${session.performer.ulid}`}
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {session.performer.display_name}
                  </Link>
                </h3>
                <StateBadge state={session.state} />
              </div>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr',
                  rowGap: 4,
                  margin: 0,
                  fontSize: '11px',
                }}
              >
                <Term>サーバー</Term>
                <Detail>
                  {session.server_slot.server_name} (slot {session.server_slot.slot}
                  {session.server_slot.pid ? ` / pid ${session.server_slot.pid}` : ''})
                </Detail>
                <Term>配信先</Term>
                <Detail>
                  {session.streaming_profile.name} ({session.streaming_profile.platform})
                </Detail>
                <Term>開始</Term>
                <Detail>{formatDateTime(session.started_at)}</Detail>
                <Term>経過</Term>
                <Detail>{formatElapsed(session.elapsed_seconds)}</Detail>
              </dl>
            </div>
            <div style={{ minWidth: 130 }}>
              <Button
                variant="danger"
                onClick={() => setForcing({ session, reason: '' })}
                style={{ padding: '6px 10px', fontSize: '11px' }}
              >
                強制停止
              </Button>
            </div>
          </div>
        </Card>
      ))}

      <Modal
        open={forcing !== null}
        title="セッションを強制停止"
        onClose={() => setForcing(null)}
        footer={
          <>
            <div style={{ minWidth: 100 }}>
              <Button variant="secondary" onClick={() => setForcing(null)}>
                キャンセル
              </Button>
            </div>
            <div style={{ minWidth: 110 }}>
              <Button
                variant="danger"
                disabled={forceStop.isPending || !forcing?.reason.trim()}
                onClick={async () => {
                  if (!forcing?.reason.trim()) return;
                  try {
                    await forceStop.mutateAsync({
                      ulid: forcing.session.ulid,
                      reason: forcing.reason.trim(),
                    });
                    setForcing(null);
                  } catch {
                    // 下で表示
                  }
                }}
              >
                {forceStop.isPending ? '実行中…' : '強制停止'}
              </Button>
            </div>
          </>
        }
      >
        {forcing && (
          <>
            <p style={{ margin: '0 0 12px', fontSize: '12px' }}>
              <strong>{forcing.session.performer.display_name}</strong> のセッションを強制停止します。
            </p>
            <Input
              label="理由(必須、500 文字以内)"
              value={forcing.reason}
              maxLength={500}
              onChange={(e) => setForcing({ session: forcing.session, reason: e.target.value })}
            />
            {forceStop.error && (
              <p
                style={{
                  margin: '0 0 0',
                  fontSize: '11px',
                  color: 'var(--color-error-text)',
                }}
              >
                {forceStop.error instanceof ApiClientError
                  ? forceStop.error.message
                  : '強制停止に失敗しました'}
              </p>
            )}
          </>
        )}
      </Modal>
    </AdminLayout>
  );
}

function StateBadge({ state }: { state: ActiveSession['state'] }) {
  const colors = {
    starting: { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)' },
    running: { fg: 'var(--color-success-text)', bg: 'rgba(34,197,94,0.12)' },
    stopping: { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)' },
    completed: { fg: 'var(--color-text-secondary)', bg: 'rgba(255,255,255,0.04)' },
    failed: { fg: 'var(--color-error-text)', bg: 'rgba(239,68,68,0.1)' },
  } as const;
  const c = colors[state];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 500,
        color: c.fg,
        background: c.bg,
        padding: '2px 8px',
        borderRadius: '3px',
      }}
    >
      {ADMIN_SESSION_STATE_LABELS[state]}
    </span>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return (
    <dt style={{ color: 'var(--color-text-tertiary)' }}>{children}</dt>
  );
}

function Detail({ children }: { children: React.ReactNode }) {
  return (
    <dd style={{ margin: 0, color: 'var(--color-text-secondary)' }}>{children}</dd>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'medium' });
}

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h} 時間 ${m} 分`;
  if (m > 0) return `${m} 分 ${s} 秒`;
  return `${s} 秒`;
}
